import { restartableTask } from 'ember-concurrency';
import IntlService from 'ember-intl/services/intl';
import _cloneDeep from 'lodash/cloneDeep';
import moment from 'moment-timezone';
import DafProfileModel, { IDAFYearly } from 'volta/models/daf-profile';
import SkuModel from 'volta/models/sku';
import {
  IDAFMonthly,
  IDAFValueMonthly,
  IDAFValueU,
  IDAFValueWeekly,
  IDAFWeekly,
  IYearZoneDef,
  IZoneDef,
  TDAFUnit,
  TDAFZoneUnit,
  TZone
} from 'volta/models/types/planning-settings';
import BvMetricsService from 'volta/services/bv-metrics';
import {
  dafIsEmpty,
  dafKeyFromZone,
  dafsByYear,
  initUnits,
  TZoneDefUnits,
  updateZones,
  YEAR_ZONE_DEF,
  yearToShallowCopy,
  ZONES_DEFS
} from 'volta/utils/daf-utils';
import { getWeeksOfYearMonth } from 'volta/utils/date-utils';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import { round } from 'volta/utils/math-utils';

import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { capitalize } from '@ember/string';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { TDafByYear } from '../../utils/daf-utils';

const DAF_UNITS: Array<{ key: TDAFUnit; label: string }> = [
  { key: 'MONTHLY', label: 'aduFrequencies.MONTHLY' },
  { key: 'WEEKLY', label: 'aduFrequencies.WEEKLY' }
];

const ZeroDaf = (size: number = 12, zero: number | null = 1) =>
  [...Array(size).keys()].reduce((acc, curr) => {
    acc[(curr + 1).toString() as keyof IDAFMonthly | keyof IDAFWeekly] = zero;
    return acc;
  }, {} as IDAFMonthly | IDAFWeekly);

interface IArgs {
  dafs: IDAFYearly;
  skuId?: string;
  disabled?: boolean;
  onUpdateMeta?: (dafUnit: TDAFUnit, yearZoneDef: Record<TZone, IZoneDef>) => void;
  onReset?: (selectedZoneKey: TZone, dafFrequencyUnit: TDAFUnit, year: number) => void;
  onChange: (
    zoneDefs: IYearZoneDef,
    zoneUnits: TZoneDefUnits,
    filledYears: number[],
    selectedDafProfile?: DafProfileModel
  ) => void;
  dafProfileId?: string;
  dafProfiles?: DafProfileModel[];
}

export default class DafCalendarEditor extends Component<IArgs> {
  @service intl!: IntlService;
  @service bvMetrics!: BvMetricsService;

  @tracked selectedZoneKey: TZone = 'adu';
  @tracked dafFrequencyUnit: TDAFUnit = 'MONTHLY';
  @tracked zoneDefs: IYearZoneDef = ZONES_DEFS;
  @tracked year = new Date().getFullYear();
  @tracked copiedYear?: { year: number; zone: TZone; frequency: TDAFUnit };
  @tracked zoneUnits = initUnits(this.args.dafs);
  @tracked autoDafError?: string;

  @tracked dafProfileId?: string = this.args.dafProfileId;

  get dafs() {
    return this.selectedDafProfile ? this.selectedDafProfile.updatePayload : this.args.dafs;
  }

  get dafProfiles() {
    return this.args.dafProfiles ?? [];
  }

  dafUnits = DAF_UNITS;

  get dafsByYear(): TDafByYear | undefined {
    return dafsByYear(this.dafs);
  }

  get dafsAreEmpty() {
    return Object.keys(this.dafsByYear ?? {}).every((year) => {
      return this.yearIsEmpty(+year);
    });
  }

  get dafCurrentYearIsEmpty() {
    return this.yearIsEmpty(this.year);
  }

  get yearZoneDefs() {
    return this.zoneDefs[this.year] ?? YEAR_ZONE_DEF;
  }

  get zoneValues() {
    return Object.values(this.yearZoneDefs);
  }

  get selectedZone() {
    return this.yearZoneDefs[this.selectedZoneKey];
  }

  get heatmapMonthly() {
    const { monthly } = this.selectedZone;
    return monthly;
  }

  get heatmapWeekly() {
    const { weekly } = this.selectedZone;
    return weekly;
  }

  get zoneUnit() {
    return this.zoneUnits[this.selectedZoneKey];
  }

  get selectedDafProfile() {
    return this.dafProfiles.findBy('id', this.dafProfileId);
  }

  get filledYears() {
    return [...new Set([...Object.keys(this.dafsByYear ?? {}), `${this.year}`])]
      .map((y) => +y)
      .sort();
  }

  // Lifecycle hooks
  // ~~~~~~

  handleInsert = () => {
    const { year, selectedZoneKey, dafFrequencyUnit } = this;
    const yearToCopy = yearToShallowCopy(this.dafsByYear ?? {}, {
      selectedYear: year,
      zoneKey: selectedZoneKey,
      dafUnit: dafFrequencyUnit
    });

    if (yearToCopy) {
      this.zoneDefs = updateZones(this.zoneDefs, dafsByYear(this.dafs) ?? {}, yearToCopy);
    }

    this.zoneDefs = updateZones(this.zoneDefs, dafsByYear(this.dafs) ?? {}, this.year, yearToCopy);
    this.args.onUpdateMeta?.(this.dafFrequencyUnit, this.yearZoneDefs);
  };

  // Actions
  // ~~~~~

  @action
  handleChange(def: IZoneDef, value: IDAFValueU) {
    const { yearZoneDefs: _zoneDefs } = this;
    const data = _zoneDefs[def.zone];

    if (!data) {
      return;
    }

    const dafValue = round(value.value);

    if (value.unit === 'MONTHLY') {
      const date = moment(value.date);
      const month = `${(value as IDAFValueMonthly).period}` as keyof IDAFMonthly;
      data.updatedMonths.push(month);

      data.monthly[month] = dafValue;

      getWeeksOfYearMonth(date.year(), date.month()).forEach((week) => {
        data.weekly[`${week}` as keyof IDAFWeekly] = dafValue;
      });
    } else {
      const week = `${(value as IDAFValueWeekly).period}` as keyof IDAFWeekly;
      data.weekly[week] = dafValue;
      data.updatedWeeks.push(week);
    }
    data.updated = true;

    const zoneDefs = { ...this.yearZoneDefs, [def.zone]: data };
    this.zoneDefs = { ...this.zoneDefs, [this.year]: _cloneDeep(zoneDefs) };
    this.onChange();
  }

  @action
  handleDafProfileChange(profile?: DafProfileModel) {
    if (profile) {
      const { dafFrequencyUnit, year, selectedZoneKey } = this;
      const profileDafsByYear = dafsByYear(profile.updatePayload)!;

      this.dafProfileId = profile.id;
      this.zoneUnits = initUnits(profile.updatePayload);

      const yearToCopy = yearToShallowCopy(profileDafsByYear, {
        selectedYear: year,
        zoneKey: selectedZoneKey,
        dafUnit: dafFrequencyUnit
      });
      let blankZones = ZONES_DEFS;
      if (yearToCopy && !blankZones[yearToCopy]) {
        blankZones = updateZones(blankZones, profileDafsByYear, yearToCopy);
      }
      this.zoneDefs = updateZones(blankZones, profileDafsByYear, year, yearToCopy);
    } else {
      this.dafProfileId = undefined;
    }

    this.onChange(false);
  }

  @action
  resetDaf(zone: TZone) {
    const { dafFrequencyUnit, year, selectedZoneKey } = this;

    const zoneDefs =
      dafFrequencyUnit === 'MONTHLY'
        ? this.updateDafMonthly(zone, {})
        : this.updateDafWeekly(zone, {});
    this.zoneDefs = { ...this.zoneDefs, [this.year]: _cloneDeep(zoneDefs) };
    this.onReset();

    const yearToCopy = yearToShallowCopy(this.dafsByYear ?? {}, {
      selectedYear: year,
      zoneKey: selectedZoneKey,
      dafUnit: dafFrequencyUnit
    });

    this.zoneDefs = updateZones(
      { ...this.zoneDefs, [this.year]: _cloneDeep(zoneDefs) },
      this.dafsByYear ?? {},
      this.year,
      yearToCopy
    );
  }

  @action
  emptyDaf(zone: TZone) {
    const { dafFrequencyUnit, zoneUnit } = this;
    const zero = zoneUnit === 'PERCENT' ? 1 : null;
    const zoneDefs =
      dafFrequencyUnit === 'MONTHLY'
        ? this.updateDafMonthly(zone, ZeroDaf(12, zero))
        : this.updateDafWeekly(zone, ZeroDaf(53, zero), null);
    this.zoneDefs = { ...this.zoneDefs, [this.year]: _cloneDeep(zoneDefs) };
    this.onChange();
  }

  @action
  onSelectZone(def: IZoneDef) {
    this.bvMetrics.trackAction(this, 'handleSelectZone');
    this.selectedZoneKey = def.zone;
  }

  @action
  handleDafCalendarChange(defs: Record<TZone, IZoneDef>) {
    const zoneDefs = this.updateZoneDefYear(defs);
    this.zoneDefs = _cloneDeep(zoneDefs);
    this.onChange();
  }

  @action
  handleReset(defs: Record<TZone, IZoneDef>) {
    this.handleEmpty(defs);
  }

  @action
  handleEmpty(defs: Record<TZone, IZoneDef>) {
    this.handleDafCalendarChange(defs);
    this.setNewYear();
  }

  @action
  onUnitChange(frequency: TDAFUnit) {
    this.bvMetrics.trackAction(this, 'onUnitChange');
    this.dafFrequencyUnit = frequency;
    this.args.onUpdateMeta?.(this.dafFrequencyUnit, this.yearZoneDefs);
  }

  /*
   * Change the zone def unit and reset dafs
   * @param {string} unit: 'PERCENT' | 'UNIT'
   */
  @action
  onUnitMeasureChange(unit: TDAFZoneUnit) {
    const zoneDefs = this.changeZoneDefByUnit(unit);
    if (!zoneDefs) {
      return;
    }
    this.zoneDefs = _cloneDeep(zoneDefs);
    this.onChange();
  }

  @action
  toNextYear() {
    this.bvMetrics.trackAction(this, 'toNextYear');
    this.year++;
    this.setNewYear();
  }

  @action
  toPreviousYear() {
    this.bvMetrics.trackAction(this, 'toPreviousYear');
    this.year--;
    this.setNewYear();
  }

  setNewYear() {
    const { year, selectedZoneKey, dafFrequencyUnit } = this;
    const yearToCopy = yearToShallowCopy(this.dafsByYear ?? {}, {
      selectedYear: year,
      zoneKey: selectedZoneKey,
      dafUnit: dafFrequencyUnit
    });
    this.zoneDefs = updateZones(this.zoneDefs, this.dafsByYear ?? {}, this.year, yearToCopy);
  }

  @action
  onCopyYear() {
    this.bvMetrics.trackAction(this, 'onCopyYear');
    this.copiedYear = {
      year: this.year,
      zone: this.selectedZoneKey,
      frequency: this.dafFrequencyUnit
    };
  }

  @action
  onPasteYear() {
    if (!this.copiedYear) {
      return;
    }
    this.bvMetrics.trackAction(this, 'onPasteYear');
    const { zoneDefs, selectedZoneKey, year } = this;
    const { year: copiedYear, zone } = this.copiedYear!;

    const defaultZone = { ...YEAR_ZONE_DEF[selectedZoneKey] };
    const zoneToPaste = zoneDefs[copiedYear][zone];

    zoneDefs[year][selectedZoneKey] = {
      ...defaultZone,
      weekly: zoneToPaste.weekly,
      monthly: zoneToPaste.monthly,
      updatedWeeks: zoneToPaste.updatedWeeks,
      updatedMonths: zoneToPaste.updatedMonths,
      updated: true
    };

    this.zoneDefs = _cloneDeep(zoneDefs);
    this.onChange();
    this.copiedYear = undefined;
  }

  updateDafMonthly(zone: TZone, dafMonthly: IDAFMonthly, zero: number | null = 1) {
    const currentYear = this.year ?? moment.utc().year();
    const data = {
      ...this.yearZoneDefs[zone],
      weekly: {} as IDAFWeekly,
      monthly: {} as IDAFMonthly
    };
    for (let month = 1; month <= 12; month++) {
      const monthKey = `${month}` as keyof IDAFMonthly;
      const daf = round(dafMonthly[monthKey]);
      data.monthly[monthKey] = daf;
      data.weekly = this.updateWeeklyValuesForYearMonth(data.weekly, daf, currentYear, monthKey);
    }
    data.updated = Object.values(dafMonthly).any((v) => v !== zero);
    return { ...this.yearZoneDefs, [zone]: data };
  }

  updateDafWeekly(zone: TZone, dafWeekly: IDAFWeekly, zero: number | null = 1) {
    const data: IZoneDef = {
      ...this.yearZoneDefs[zone],
      weekly: {} as IDAFWeekly,
      updated: false,
      updatedWeeks: []
    };

    Object.keys(dafWeekly).forEach((key: keyof IDAFWeekly) => {
      data.weekly[key] = round(dafWeekly[key]);
      if (data.weekly[key] !== zero) {
        data.updated = true;
        data.updatedWeeks.push(key);
      } else {
        delete data.updatedWeeks[key];
      }
    });

    return { ...this.yearZoneDefs, [zone]: data };
  }

  updateDafWeeklyFromMonthly(zone: TZone, dafMonthly: IDAFMonthly) {
    const currentYear = this.year ?? moment.utc().year();
    const data = { ...this.yearZoneDefs[zone], weekly: {} as IDAFWeekly };
    Object.keys(dafMonthly).forEach((key: keyof IDAFMonthly) => {
      data.weekly = this.updateWeeklyValuesForYearMonth(
        data.weekly,
        round(dafMonthly[key]),
        currentYear,
        key
      );
    });
    data.updated = Object.entries(dafMonthly).length > 0;
    return { ...this.yearZoneDefs, [zone]: data };
  }

  updateWeeklyValuesForYearMonth(
    weekly: IDAFWeekly,
    daf: number | undefined,
    year: number,
    month: keyof IDAFMonthly
  ) {
    const result = { ...weekly };
    const weeks = getWeeksOfYearMonth(year, parseInt(month) - 1);
    weeks.forEach((w: number) => {
      result[`${w}` as keyof IDAFWeekly] = daf;
    });
    return result;
  }

  autoDafTask = restartableTask(async () => {
    try {
      const { dafFrequencyUnit } = this;

      const params = {
        skuId: this.args.skuId,
        years: 1
      };

      this.autoDafError = undefined;

      const response: {
        data: { dafMonthly: IDAFMonthly; dafWeekly: IDAFWeekly };
      } = await SkuModel.calculateAutoDaf(params);

      if (
        JSON.stringify(response.data.dafMonthly) === '{}' &&
        JSON.stringify(response.data.dafWeekly) === '{}'
      ) {
        this.autoDafError = this.intl.t('dafProfilesPage.autoDafError');
        setTimeout(() => {
          this.autoDafError = undefined;
        }, 10000);
      } else {
        const year = new Date().getFullYear().toString();
        const zoneDefs =
          dafFrequencyUnit === 'MONTHLY'
            ? this.updateDafMonthly(
                'adu',
                (response.data.dafMonthly as Record<string, IDAFMonthly>)[year]
              )
            : this.updateDafWeekly(
                'adu',
                (response.data.dafWeekly as Record<string, IDAFWeekly>)[year]
              );

        this.zoneDefs = { ...this.zoneDefs, [year]: _cloneDeep(zoneDefs) };
        this.onChange();
      }

      return response;
    } catch (error) {
      this.autoDafError = undefined;
      return defaultErrorHandler(error);
    }
  });

  updateZoneDefYear(defs: Record<TZone, IZoneDef>) {
    this.bvMetrics.trackAction(this, 'updateZoneDefYear');
    const { zoneDefs, year } = this;
    zoneDefs[year] = defs;
    return _cloneDeep(zoneDefs);
  }

  changeZoneDefByUnit(unit: 'PERCENT' | 'UNIT') {
    if (unit === this.zoneUnits[this.selectedZoneKey]) {
      return undefined;
    }

    this.bvMetrics.trackAction(this, 'handleZoneDefUnitChange');
    this.zoneUnits = { ...this.zoneUnits, [this.selectedZoneKey]: unit };

    const { zoneDefs: _zoneDefs, selectedZoneKey } = this;
    const zoneDefs: IYearZoneDef = {};
    Object.keys(_zoneDefs).forEach((year) => {
      const yearZoneDef = _zoneDefs[+year] ?? YEAR_ZONE_DEF;

      zoneDefs[+year] = {
        ...yearZoneDef,
        [selectedZoneKey]: {
          ...yearZoneDef[selectedZoneKey],
          weekly: {},
          monthly: {}
        }
      };
    });

    return _cloneDeep(zoneDefs);
  }

  // Helpers
  getDaf = (dp: DafProfileModel) => {
    const { entries, keys } = Object;
    const { dafFrequencyUnit, selectedZoneKey } = this;
    const dpKey = dafKeyFromZone(selectedZoneKey) + capitalize(dafFrequencyUnit.toLowerCase());
    const dafYears = dp[dpKey as keyof IDAFYearly];
    const filledYears = entries(dafYears)
      .filter(([, daf]) => !!keys(daf).length)
      .map(([year]) => year);
    let latestYear: string | number | undefined = filledYears.sort().lastObject;
    latestYear = parseInt(latestYear ? `${latestYear}` : `${this.year}`);
    const daf = dafYears[latestYear];
    const isMonthly = dafFrequencyUnit === 'MONTHLY';

    return {
      dafMonthly: isMonthly ? daf : undefined,
      dafWeekly: isMonthly ? undefined : daf
    };
  };

  // Private
  private onChange(withProfileReset: boolean = true) {
    if (withProfileReset) {
      this.dafProfileId = undefined;
    }
    this.args.onChange(this.zoneDefs, this.zoneUnits, this.filledYears, this.selectedDafProfile);
    this.args.onUpdateMeta?.(this.dafFrequencyUnit, { ...this.yearZoneDefs });
  }

  private onReset() {
    this.dafProfileId = undefined;

    this.args.onReset?.(this.selectedZoneKey, this.dafFrequencyUnit, this.year);
    this.args.onUpdateMeta?.(this.dafFrequencyUnit, { ...this.yearZoneDefs });
  }

  private yearIsEmpty(year: number) {
    const dafYear = this.dafsByYear?.[year];

    if (dafYear) {
      const dafWeekly = this.dafYearByPeriod(year, 'Weekly');
      const dafMonthly = this.dafYearByPeriod(year, 'Monthly');
      return (
        (this.dafFrequencyUnit === 'WEEKLY' && dafIsEmpty(dafWeekly, year)) ||
        (this.dafFrequencyUnit === 'MONTHLY' && dafIsEmpty(dafMonthly))
      );
    }
    return true;
  }

  private dafYearByPeriod(year: number, period: 'Weekly' | 'Monthly'): IDAFWeekly | IDAFMonthly {
    const dafKey = dafKeyFromZone(this.selectedZoneKey);

    return (this.dafsByYear?.[year][(dafKey + period) as keyof IDAFYearly] ?? {}) as
      | IDAFWeekly
      | IDAFMonthly;
  }
}
